import React, { useState } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis } from 'recharts';
import { Activity, Zap, Leaf, TrendingUp } from 'lucide-react';

function AnalysisResults({ analysis }) {
  const [selectedJump, setSelectedJump] = useState(null);

  if (!analysis || !analysis.jumps || analysis.jumps.length === 0) {
    return (
      <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6">
        <p className="text-gray-400">No analysis results available</p>
      </div>
    );
  }

  // Aggregate statistics
  const avgSimilarity = (
    analysis.jumps.reduce((sum, j) => sum + (j.similarity || 0), 0) / analysis.jumps.length
  ).toFixed(4);

  const totalSOPs = analysis.jumps.reduce((sum, j) => sum + (j.sops || 0), 0);
  const totalEnergy = analysis.jumps.reduce((sum, j) => sum + (j.energy_joules || 0), 0);
  const totalCO2 = analysis.jumps.reduce((sum, j) => sum + (j.co2_kg || 0), 0);

  // Prepare chart data
  const similarityDistribution = analysis.jumps.map((jump, idx) => ({
    index: idx,
    similarity: jump.similarity || 0,
    sops: jump.sops || 0,
    trajectory: jump.trajectory_id || 0
  }));

  // Top 10 most interesting jumps
  const sortedJumps = [...analysis.jumps].sort((a, b) => 
    Math.abs((b.similarity || 0) - 0.5) - Math.abs((a.similarity || 0) - 0.5)
  );
  const topJumps = sortedJumps.slice(0, 10);

  return (
    <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6">
      <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Activity className="w-6 h-6 text-green-400" />
        V3 Spike-Only Analysis Results
      </h3>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 rounded-lg p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-purple-400" />
            <p className="text-sm text-gray-300">Jumps Analyzed</p>
          </div>
          <p className="text-3xl font-bold">{analysis.analyzed_jumps}</p>
          <p className="text-xs text-gray-400 mt-1">
            of {analysis.total_jumps} detected
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-lg p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            <p className="text-sm text-gray-300">Avg Similarity</p>
          </div>
          <p className="text-3xl font-bold">{avgSimilarity}</p>
          <p className="text-xs text-gray-400 mt-1">
            spike-only cosine
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-lg p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-green-400" />
            <p className="text-sm text-gray-300">Total Energy</p>
          </div>
          <p className="text-3xl font-bold">{(totalEnergy * 1e6).toFixed(0)}</p>
          <p className="text-xs text-gray-400 mt-1">
            µJ ({totalSOPs.toLocaleString()} SOPs)
          </p>
        </div>

        <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 rounded-lg p-4 border border-emerald-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Leaf className="w-5 h-5 text-emerald-400" />
            <p className="text-sm text-gray-300">Carbon Footprint</p>
          </div>
          <p className="text-3xl font-bold">{(totalCO2 * 1e9).toFixed(2)}</p>
          <p className="text-xs text-gray-400 mt-1">
            nanograms CO₂
          </p>
        </div>
      </div>

      {/* Similarity Distribution Chart */}
      <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
        <h4 className="text-lg font-semibold mb-4">Similarity Distribution</h4>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="index" 
              name="Jump Index" 
              stroke="#9ca3af"
              label={{ value: 'Jump Index', position: 'insideBottom', offset: -5, fill: '#9ca3af' }}
            />
            <YAxis 
              dataKey="similarity" 
              name="Similarity" 
              stroke="#9ca3af"
              label={{ value: 'Similarity', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
            />
            <ZAxis dataKey="sops" range={[50, 400]} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
              labelStyle={{ color: '#9ca3af' }}
              formatter={(value, name) => [
                name === 'similarity' ? value.toFixed(4) : value,
                name === 'similarity' ? 'Similarity' : name === 'sops' ? 'SOPs' : name
              ]}
            />
            <Scatter 
              name="Quantum Jumps" 
              data={similarityDistribution} 
              fill="#8b5cf6"
              onClick={(data) => setSelectedJump(data)}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Top 10 Most Interesting Jumps */}
      <div className="bg-gray-900/50 rounded-lg p-4">
        <h4 className="text-lg font-semibold mb-4">Top 10 Most Interesting Jumps</h4>
        <div className="space-y-2">
          {topJumps.map((jump, idx) => (
            <div 
              key={idx}
              className="bg-gray-800 rounded-lg p-3 flex items-center justify-between hover:bg-gray-700 transition-colors cursor-pointer"
              onClick={() => setSelectedJump(jump)}
            >
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold text-purple-400">#{idx + 1}</span>
                <div>
                  <p className="font-semibold">
                    Trajectory {jump.trajectory_id || 0} @ t={(jump.jump_time || jump.time || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-400">
                    Similarity: {(jump.similarity || 0).toFixed(4)} | 
                    SOPs: {jump.sops || 0} | 
                    Spikes: {jump.pre_spikes || 0} → {jump.post_spikes || 0}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-400">
                  {((jump.energy_joules || 0) * 1e6).toFixed(2)} µJ
                </p>
                <p className="text-xs text-emerald-400">
                  {((jump.co2_kg || 0) * 1e9).toFixed(4)} ng CO₂
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Jump Details Modal */}
      {selectedJump && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedJump(null)}
        >
          <div 
            className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full border border-purple-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Jump Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-400">Trajectory ID</p>
                <p className="text-lg font-semibold">{selectedJump.trajectory_id || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Jump Time</p>
                <p className="text-lg font-semibold">{(selectedJump.jump_time || selectedJump.time || 0).toFixed(4)} s</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Similarity</p>
                <p className="text-lg font-semibold">{(selectedJump.similarity || 0).toFixed(6)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">SOPs</p>
                <p className="text-lg font-semibold">{selectedJump.sops || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Pre-jump Spikes</p>
                <p className="text-lg font-semibold">{selectedJump.pre_spikes || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Post-jump Spikes</p>
                <p className="text-lg font-semibold">{selectedJump.post_spikes || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Energy</p>
                <p className="text-lg font-semibold text-green-400">
                  {((selectedJump.energy_joules || 0) * 1e6).toFixed(4)} µJ
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">CO₂</p>
                <p className="text-lg font-semibold text-emerald-400">
                  {((selectedJump.co2_kg || 0) * 1e9).toFixed(6)} ng
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedJump(null)}
              className="mt-6 w-full py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalysisResults;

